/**
 * Shared display formatters.
 *
 * Use these instead of inlining a separator. The site had three different pipe
 * joins (`" | "`, `"  |  "`, and one more) drifting apart in the same file.
 */

/**
 * Render a list of facts as bracketed tokens:
 *
 *   ["Leadership", "Research Funds"]  ->  "[Leadership] [Research Funds]"
 *
 * This replaced `join(" | ")`. A pipe reads as a table rule rather than as a
 * boundary between values, and it collided with the actual vertical rules in
 * the header. Brackets make each value its own object without needing a chip
 * component inside a caption-sized line.
 *
 * Empty, null and whitespace-only entries are dropped rather than rendered as
 * an empty `[]`, so callers can pass conditional values directly.
 */
export const bracketed = (values) =>
  (values ?? [])
    .filter((v) => v !== null && v !== undefined && String(v).trim() !== "")
    .map((v) => `[${String(v).trim()}]`)
    .join(" ");

const _date = (iso) => {
  const date = iso ? new Date(iso) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

/** A timestamp as "Aug 15, 2026, 8:07 AM" in the reader's time zone; "" when missing or unreadable. */
export const formatDateTime = (iso) => {
  const date = _date(iso);
  return date
    ? date.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
    : "";
};

/** How long ago a timestamp was, in the largest whole unit: "just now", "5 minutes ago", "3 hours ago", "yesterday", "56 days ago". */
export const formatTimeAgo = (iso, now = new Date()) => {
  const date = _date(iso);
  if (!date) return "";
  const minutes = Math.floor(Math.max(0, now.getTime() - date.getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
};
